import {
  Boxes,
  Download,
  FileText,
  ReceiptText,
  RefreshCw,
  TriangleAlert,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  type BLRelatedDocumentItem,
  type IngestedAnalysisPairItem,
  listIngestedAnalysisPairs,
  type ReconcileBLResult,
  type ReconcileDuplicatePair,
  type ReconcileDuplicatePairDetail,
  type ReconcileMismatchDetail,
} from "@/lib/api/extract";
import { cn } from "@/lib/utils";
import {
  type DetailTab,
  getActiveDetail,
  getIssueCounts,
  statusClass,
  statusLabel,
} from "./reconcile-helpers";
import type { BlDocumentsState } from "./useBlDocuments";

type VerifyBlInspectorContentProps = {
  selectedItem: ReconcileBLResult;
  detailTab: DetailTab;
  layoutMode?: "regular" | "compact";
  documentsState: BlDocumentsState;
  downloadingSha: string | null;
  downloadingReportSha: string | null;
  downloadErrorMessage: string | null;
  onDetailTabChange: (tab: DetailTab) => void;
  onRetryDocuments: () => void;
  onDownloadDocument: (document: BLRelatedDocumentItem) => Promise<void>;
  onDownloadDocumentReport: (document: BLRelatedDocumentItem) => Promise<void>;
};

const formatDateTime = new Intl.DateTimeFormat("it-IT", {
  dateStyle: "short",
  timeStyle: "short",
});

function formatBytes(value: number | null | undefined): string {
  if (!value || value <= 0) return "Dimensione n.d.";

  const units = ["B", "KB", "MB", "GB"];
  let size = value;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  const rounded =
    size >= 10 || unitIndex === 0 ? size.toFixed(0) : size.toFixed(1);
  return `${rounded} ${units[unitIndex]}`;
}

function formatSeenAt(value: string): string {
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return "Ultima acquisizione n.d.";
  return `Ultima acquisizione ${formatDateTime.format(parsed)}`;
}

function getDetailEmptyMessage(detailTab: DetailTab): string {
  if (detailTab === "missing") {
    return "Nessun container mancante per questo BL.";
  }

  if (detailTab === "extra") {
    return "Nessun container extra per questo BL.";
  }

  if (detailTab === "found") {
    return "Nessun container trovato per questo BL.";
  }

  return "Nessun elemento in questa categoria.";
}

function getDocumentReportAvailability(item: BLRelatedDocumentItem): boolean {
  return Boolean(item.has_analysis);
}

function DocumentBadges({ item }: { item: BLRelatedDocumentItem }) {
  return (
    <div className="verify-documents__badges">
      {item.has_polizza ? (
        <span className="verify-document-badge verify-document-badge--polizza">
          <Boxes size={13} aria-hidden="true" />
          Polizza
        </span>
      ) : null}
      {item.has_analysis ? (
        <span className="verify-document-badge verify-document-badge--analysis">
          <ReceiptText size={13} aria-hidden="true" />
          Fatture/DDT
        </span>
      ) : null}
    </div>
  );
}

function MismatchDetailsList({
  details,
  emptyMessage,
}: {
  details: ReconcileMismatchDetail[];
  emptyMessage: string;
}) {
  if (details.length === 0) return null;

  return (
    <ul className="verify-detail-list">
      {details.map((detail) => {
        const invoiceNumbers = detail.invoice_numbers ?? [];
        return (
          <li
            key={`mismatch-detail-${detail.container}`}
            className="verify-detail-list__item"
          >
            <span className="verify-detail-list__title">
              Container {detail.container}
            </span>
            {invoiceNumbers.length > 0 ? (
              <span className="verify-detail-list__meta">
                Fatture collegate: {invoiceNumbers.join(", ")}
              </span>
            ) : (
              <span className="verify-detail-list__meta verify-detail-list__meta--muted">
                {emptyMessage}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function DuplicatePairsPanel({
  duplicatePairs,
  duplicatePairDetails,
}: {
  duplicatePairs: ReconcileDuplicatePair[];
  duplicatePairDetails: ReconcileDuplicatePairDetail[];
}) {
  if (duplicatePairDetails.length > 0) {
    return (
      <ul className="verify-duplicate-list">
        {duplicatePairDetails.map((detail) => {
          const fatture = detail.fatture ?? [];
          return (
            <li
              key={`${detail.bl}-${detail.container}`}
              className="verify-duplicate-card"
            >
              <div className="verify-duplicate-card__header">
                <span className="verify-duplicate-card__title">
                  Container {detail.container}
                </span>
                <span className="verify-duplicate-card__meta">
                  {fatture.length} fatture collegate
                </span>
              </div>

              {fatture.length > 0 ? (
                <ul className="verify-duplicate-card__fatture">
                  {fatture.map((fattura) => (
                    <li
                      key={`${detail.container}-${fattura.fattura_id}`}
                      className="verify-duplicate-card__fattura"
                    >
                      <div className="verify-duplicate-card__fattura-main">
                        <span className="verify-duplicate-card__fattura-title">
                          {fattura.invoice_number || "Fattura n.d."}
                        </span>
                        <span className="verify-duplicate-card__fattura-meta">
                          Data: {fattura.invoice_date || "n.d."}
                        </span>
                        <span className="verify-duplicate-card__fattura-meta">
                          Pagina:{" "}
                          {fattura.invoice_page != null
                            ? fattura.invoice_page
                            : "n.d."}
                        </span>
                      </div>
                      <div className="verify-duplicate-card__fattura-secondary">
                        <span className="verify-duplicate-card__fattura-meta">
                          Documento: {fattura.input_filename || "n.d."}
                        </span>
                        <span className="verify-duplicate-card__fattura-meta">
                          SHA: {fattura.source_document_sha256}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="verify-duplicate-card__empty">
                  Nessun riferimento fattura disponibile per questo container.
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  if (duplicatePairs.length === 0) {
    return (
      <div className="verify-empty-state">
        <FileText size={18} aria-hidden="true" />
        <p>Nessun duplicato trovato per questo BL.</p>
      </div>
    );
  }

  return (
    <ul className="verify-simple-list">
      {duplicatePairs.map((dup) => (
        <li
          key={`${dup.bl}-${dup.container}`}
          className="verify-simple-list__item"
        >
          <span className="verify-simple-list__title">
            Container {dup.container}
          </span>
          <span className="verify-simple-list__meta">
            Fatture: {dup.fatture_count ?? 0} · Source docs:{" "}
            {dup.source_documents_count ?? 0}
          </span>
          <span className="verify-simple-list__meta">
            Invoice: {(dup.invoice_numbers ?? []).join(", ") || "n.d."}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function VerifyBlInspectorContent({
  selectedItem,
  detailTab,
  documentsState,
  downloadingSha,
  downloadingReportSha,
  downloadErrorMessage,
  onDetailTabChange,
  onRetryDocuments,
  onDownloadDocument,
  onDownloadDocumentReport,
}: VerifyBlInspectorContentProps) {
  const activeDetail = getActiveDetail(selectedItem, detailTab);
  const documentItems = documentsState.data?.items ?? [];
  const documentsCount =
    documentsState.data?.total_documents ?? documentItems.length;
  const issueCounts = getIssueCounts(selectedItem);
  const currentTabId = `verify-tab-${detailTab}`;
  const currentPanelId = `verify-panel-${detailTab}`;
  const hasMismatch =
    issueCounts.ddtMismatch > 0 || issueCounts.fatturaMismatch > 0;
  const duplicatePairs = selectedItem.duplicate_pairs ?? [];
  const duplicatePairDetails = selectedItem.duplicate_pairs_details ?? [];
  const ddtMismatchDetails = selectedItem.ddt_mismatch_details ?? [];
  const fatturaMismatchDetails = selectedItem.fattura_mismatch_details ?? [];

  return (
    <>
      <div className="verify-inspector__topbar">
        <div className="verify-inspector__header">
          <div className="verify-inspector__title-block">
            <span className="verify-inspector__eyebrow">BL selezionato</span>
            <h3 className="status-panel__section-title">{selectedItem.bl}</h3>
          </div>
          <span
            className={cn(
              "verify-status-badge",
              statusClass(selectedItem.status),
            )}
          >
            {statusLabel(selectedItem.status)}
          </span>
        </div>

        <div
          className="verify-inspector__tabs"
          role="tablist"
          aria-label="Categorie dettaglio BL"
        >
          <button
            id="verify-tab-found"
            type="button"
            role="tab"
            aria-controls="verify-panel-found"
            aria-selected={detailTab === "found"}
            className={cn(
              "verify-inspector__tab",
              detailTab === "found" ? "verify-inspector__tab--active" : "",
            )}
            onClick={() => onDetailTabChange("found")}
          >
            <span>Trovati</span>
            <span className="verify-inspector__tab-badge verify-inspector__tab-badge--success">
              {issueCounts.found}
            </span>
          </button>
          <button
            id="verify-tab-missing"
            type="button"
            role="tab"
            aria-controls="verify-panel-missing"
            aria-selected={detailTab === "missing"}
            className={cn(
              "verify-inspector__tab",
              detailTab === "missing" ? "verify-inspector__tab--active" : "",
            )}
            onClick={() => onDetailTabChange("missing")}
          >
            <span>Mancanti</span>
            <span className="verify-inspector__tab-badge">
              {issueCounts.missing}
            </span>
          </button>
          <button
            id="verify-tab-extra"
            type="button"
            role="tab"
            aria-controls="verify-panel-extra"
            aria-selected={detailTab === "extra"}
            className={cn(
              "verify-inspector__tab",
              detailTab === "extra" ? "verify-inspector__tab--active" : "",
            )}
            onClick={() => onDetailTabChange("extra")}
          >
            <span>Extra</span>
            <span className="verify-inspector__tab-badge">
              {issueCounts.extra}
            </span>
          </button>
          <button
            id="verify-tab-duplicates"
            type="button"
            role="tab"
            aria-controls="verify-panel-duplicates"
            aria-selected={detailTab === "duplicates"}
            className={cn(
              "verify-inspector__tab",
              detailTab === "duplicates" ? "verify-inspector__tab--active" : "",
            )}
            onClick={() => onDetailTabChange("duplicates")}
          >
            <span>Doppi</span>
            <span className="verify-inspector__tab-badge verify-inspector__tab-badge--risk">
              {issueCounts.duplicates}
            </span>
          </button>
          <button
            id="verify-tab-documents"
            type="button"
            role="tab"
            aria-controls="verify-panel-documents"
            aria-selected={detailTab === "documents"}
            className={cn(
              "verify-inspector__tab",
              detailTab === "documents" ? "verify-inspector__tab--active" : "",
            )}
            onClick={() => onDetailTabChange("documents")}
          >
            <span>Documenti</span>
            <span className="verify-inspector__tab-badge">
              {documentsState.loading && !documentsState.hasLoaded
                ? "…"
                : documentsCount}
            </span>
          </button>
          <button
            id="verify-tab-mismatches"
            type="button"
            role="tab"
            aria-controls="verify-panel-mismatches"
            aria-selected={detailTab === "mismatches"}
            className={cn(
              "verify-inspector__tab",
              "verify-inspector__tab--warning",
              detailTab === "mismatches" ? "verify-inspector__tab--active" : "",
            )}
            onClick={() => onDetailTabChange("mismatches")}
          >
            <span>Mismatches</span>
            <span className="verify-inspector__tab-badge verify-inspector__tab-badge--warning">
              {issueCounts.ddtMismatch + issueCounts.fatturaMismatch}
            </span>
          </button>
        </div>
      </div>

      <div
        id={currentPanelId}
        className="verify-inspector__panel"
        role="tabpanel"
        aria-labelledby={currentTabId}
      >
        {" "}
        {detailTab === "found" ? (
          <FoundContainersList bl={selectedItem.bl} containers={activeDetail} />
        ) : null}
        {detailTab === "duplicates" ? (
          <DuplicatePairsPanel
            duplicatePairs={duplicatePairs}
            duplicatePairDetails={duplicatePairDetails}
          />
        ) : null}
        {detailTab === "documents" ? (
          <div className="verify-documents">
            <div className="verify-documents__header">
              <p className="verify-documents__copy">
                Recupera i file sorgente collegati al BL e scarica il PDF
                originale.
              </p>
              {documentsState.errorMessage ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="verify-documents__retry"
                  onClick={onRetryDocuments}
                >
                  <RefreshCw size={14} aria-hidden="true" />
                  Riprova
                </Button>
              ) : null}
            </div>

            {downloadErrorMessage ? (
              <p className="upload-form__error" role="alert">
                Errore: {downloadErrorMessage}
              </p>
            ) : null}

            {documentsState.loading && !documentsState.hasLoaded ? (
              <div className="verify-documents__skeletons" aria-hidden="true">
                {[0, 1, 2].map((index) => (
                  <div
                    key={`document-skeleton-${index}`}
                    className="verify-document-card verify-document-card--skeleton"
                  />
                ))}
              </div>
            ) : null}

            {!documentsState.loading && documentsState.errorMessage ? (
              <p className="upload-form__error" role="alert">
                Errore: {documentsState.errorMessage}
              </p>
            ) : null}

            {!documentsState.loading &&
            !documentsState.errorMessage &&
            documentItems.length === 0 ? (
              <div className="verify-empty-state">
                <FileText size={18} aria-hidden="true" />
                <p>Nessun documento collegato a questo BL.</p>
              </div>
            ) : null}

            {!documentsState.loading &&
            !documentsState.errorMessage &&
            documentItems.length > 0 ? (
              <ul className="verify-documents__list">
                {documentItems.map((document) => {
                  const isDownloading =
                    downloadingSha === document.source_document_sha256;
                  const isDownloadingReport =
                    downloadingReportSha === document.source_document_sha256;
                  const canDownloadReport =
                    getDocumentReportAvailability(document);
                  return (
                    <li
                      key={document.source_document_sha256}
                      className="verify-document-card"
                    >
                      <div className="verify-document-card__body">
                        <div className="verify-document-card__topline">
                          <DocumentBadges item={document} />
                          <span className="verify-document-card__meta">
                            {formatBytes(document.file_size)}
                          </span>
                        </div>

                        <div className="verify-document-card__heading">
                          <strong
                            title={
                              document.input_filename ??
                              document.source_document_sha256
                            }
                          >
                            {document.input_filename ??
                              `Documento ${document.source_document_id}`}
                          </strong>
                          <span className="verify-document-card__meta">
                            {formatSeenAt(document.last_seen_at)}
                          </span>
                        </div>

                        <div className="verify-document-card__stats">
                          <span>
                            Match polizza {document.polizza_pairs_count ?? 0}
                          </span>
                          <span>
                            Match analisi {document.analysis_pairs_count ?? 0}
                          </span>
                          <span>Acquisizioni {document.seen_count ?? 0}</span>
                        </div>
                      </div>

                      <div className="verify-document-card__actions">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="verify-document-card__download"
                          onClick={() => void onDownloadDocument(document)}
                          disabled={isDownloading}
                          aria-label={`Scarica ${document.input_filename ?? document.source_document_sha256}`}
                        >
                          <Download size={14} aria-hidden="true" />
                          {isDownloading ? "Scarico…" : "Scarica PDF"}
                        </Button>

                        {canDownloadReport ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="verify-document-card__download"
                            onClick={() =>
                              void onDownloadDocumentReport(document)
                            }
                            disabled={isDownloadingReport}
                            aria-label={`Scarica report di ${document.input_filename ?? document.source_document_sha256}`}
                          >
                            <ReceiptText size={14} aria-hidden="true" />
                            {isDownloadingReport
                              ? "Scarico…"
                              : "Scarica report"}
                          </Button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        ) : null}
        {detailTab === "mismatches" ? (
          hasMismatch ? (
            <section
              className="verify-mismatch-alert verify-mismatch-alert--panel"
              aria-label="Avvisi mismatch documento"
            >
              <div className="verify-mismatch-alert__header">
                <span className="verify-mismatch-alert__icon">
                  <TriangleAlert size={16} aria-hidden="true" />
                </span>
                <div>
                  <strong className="verify-mismatch-alert__title">
                    Anomalie tra DDT e fatture
                  </strong>
                  <p className="verify-mismatch-alert__copy">
                    Il BL contiene container presenti su un documento ma con
                    mismatch sull&apos;altro.
                  </p>
                </div>
              </div>

              <div className="verify-mismatch-alert__groups">
                {issueCounts.ddtMismatch > 0 ? (
                  <div className="verify-mismatch-alert__group">
                    <span className="verify-mismatch-alert__label">
                      Mismatch DDT ({issueCounts.ddtMismatch})
                    </span>
                    <p className="verify-mismatch-alert__description">
                      Corretti in fattura, errati o mancanti sul DDT.
                    </p>
                    <MismatchDetailsList
                      details={ddtMismatchDetails}
                      emptyMessage="Nessun numero fattura disponibile."
                    />
                  </div>
                ) : null}

                {issueCounts.fatturaMismatch > 0 ? (
                  <div className="verify-mismatch-alert__group">
                    <span className="verify-mismatch-alert__label">
                      Mismatch fattura ({issueCounts.fatturaMismatch})
                    </span>
                    <p className="verify-mismatch-alert__description">
                      Corretti sul DDT, errati o mancanti in fattura.
                    </p>
                    <MismatchDetailsList
                      details={fatturaMismatchDetails}
                      emptyMessage="Nessun numero fattura associato."
                    />
                  </div>
                ) : null}
              </div>
            </section>
          ) : (
            <div className="verify-empty-state verify-empty-state--warning">
              <TriangleAlert size={18} aria-hidden="true" />
              <p>Nessun mismatch tra DDT e fatture per questo BL.</p>
            </div>
          )
        ) : null}
        {detailTab !== "duplicates" &&
        detailTab !== "documents" &&
        detailTab !== "mismatches" &&
        detailTab !== "found" ? (
          activeDetail.length === 0 ? (
            <div className="verify-empty-state">
              <FileText size={18} aria-hidden="true" />
              <p>{getDetailEmptyMessage(detailTab)}</p>
            </div>
          ) : (
            <ul className="verify-simple-list">
              {activeDetail.map((container) => (
                <li
                  key={`${detailTab}-${container}`}
                  className="verify-simple-list__item"
                >
                  <span className="verify-simple-list__title">{container}</span>
                </li>
              ))}
            </ul>
          )
        ) : null}
      </div>
    </>
  );
}

function FoundContainersList({
  bl,
  containers,
}: {
  bl: string;
  containers: string[];
}) {
  const [pairs, setPairs] = useState<IngestedAnalysisPairItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    async function fetchPairs() {
      if (!bl || containers.length === 0) {
        setPairs([]);
        setErrorMessage(null);
        return;
      }
      setLoading(true);
      setErrorMessage(null);
      try {
        const res = await listIngestedAnalysisPairs({ bl, limit: 1000 });
        if (active) {
          setPairs(res.items ?? []);
        }
      } catch (_err) {
        if (active) {
          setErrorMessage("Impossibile recuperare i dettagli fattura.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }
    void fetchPairs();
    return () => {
      active = false;
    };
  }, [bl, containers.length]);

  if (containers.length === 0) {
    return (
      <div className="verify-empty-state">
        <FileText size={18} aria-hidden="true" />
        <p>{getDetailEmptyMessage("found")}</p>
      </div>
    );
  }

  const visiblePairs = pairs.filter((pair) =>
    containers.includes(pair.container),
  );

  return (
    <ul className="verify-simple-list">
      {loading ? (
        <li className="verify-simple-list__item">
          <span className="verify-simple-list__meta">
            Caricamento dati fattura...
          </span>
        </li>
      ) : errorMessage ? (
        <li className="verify-simple-list__item">
          <span className="verify-simple-list__meta">{errorMessage}</span>
        </li>
      ) : visiblePairs.length > 0 ? (
        visiblePairs.map((pair) => (
          <li key={pair.pair_id} className="verify-simple-list__item">
            <span className="verify-simple-list__title">
              Container {pair.container}
            </span>
            <span className="verify-simple-list__meta">
              Fattura: {pair.invoice_number || "n.d."} (Doc:{" "}
              {pair.input_filename || "n.d."})
            </span>
          </li>
        ))
      ) : (
        <li className="verify-simple-list__item">
          <span className="verify-simple-list__meta">
            Nessuna fattura trovata
          </span>
        </li>
      )}
    </ul>
  );
}
